import {
  CHAT_LIMITS,
  createChatMessage,
  countCodePoints,
  parseChatMessage,
  type AnyChatWireMessage,
} from '@screenshare/protocol';
import type { PeerRole } from '@screenshare/protocol';
import { TypedEventEmitter } from '../utils/TypedEventEmitter';
import { NOOP_LOGGER, writeLog, type StructuredLogger } from '../webrtc/logger';
import { sanitizeChatText } from './sanitize';
import {
  ChatError,
  type ChatChannelState,
  type ChatEntry,
  type ChatEvents,
  type ChatSendResult,
  type ChatSnapshot,
} from './types';

export const CHAT_CHANNEL_LABEL = 'chat';
export const CHAT_CHANNEL_PROTOCOL = 'screenshare-chat-v1';

const CHAT_HISTORY_LIMIT = 500;
const DEDUPLICATION_LIMIT = 1_000;
const LOCAL_TYPING_IDLE_MS = 2_000;
const REMOTE_TYPING_EXPIRY_MS = 5_000;
const MAX_BUFFERED_AMOUNT = 256 * 1_024;

export interface DataChannelManagerOptions {
  readonly role: PeerRole;
  readonly logger?: StructuredLogger;
  readonly now?: () => number;
  readonly createMessageId?: () => string;
  readonly localTypingIdleMs?: number;
  readonly remoteTypingExpiryMs?: number;
  readonly maxBufferedAmount?: number;
}

export class DataChannelManager extends TypedEventEmitter<ChatEvents> {
  private readonly logger: StructuredLogger;
  private readonly now: () => number;
  private readonly createMessageId: () => string;
  private readonly messages: ChatEntry[] = [];
  private readonly seenMessageIds = new Set<string>();
  private readonly seenMessageOrder: string[] = [];
  private connection: RTCPeerConnection | null = null;
  private channel: RTCDataChannel | null = null;
  private localTypingTimer: ReturnType<typeof setTimeout> | null = null;
  private remoteTypingTimer: ReturnType<typeof setTimeout> | null = null;
  private localTyping = false;
  private peerTyping = false;
  private panelVisible = false;
  private unreadCount = 0;
  private _state: ChatChannelState = 'idle';

  constructor(private readonly options: DataChannelManagerOptions) {
    super();
    this.logger = options.logger ?? NOOP_LOGGER;
    this.now = options.now ?? Date.now;
    this.createMessageId = options.createMessageId ?? (() => crypto.randomUUID());
  }

  get state(): ChatChannelState {
    return this._state;
  }

  get snapshot(): ChatSnapshot {
    return {
      state: this._state,
      messages: [...this.messages],
      unreadCount: this.unreadCount,
      peerTyping: this.peerTyping,
    };
  }

  subscribe(listener: (snapshot: ChatSnapshot) => void): () => void {
    listener(this.snapshot);
    return this.on('change', ({ snapshot }) => listener(snapshot));
  }

  bindPeerConnection(connection: RTCPeerConnection): void {
    if (this._state === 'ended') {
      throw new ChatError('invalid-channel', 'Cannot bind chat after the session ended.');
    }

    const reconnecting = this._state !== 'idle';
    this.releaseConnection();
    this.connection = connection;
    this.setState(reconnecting ? 'reconnecting' : 'connecting');

    if (this.options.role === 'host') {
      const channel = connection.createDataChannel(CHAT_CHANNEL_LABEL, {
        ordered: true,
        protocol: CHAT_CHANNEL_PROTOCOL,
      });
      this.attachChannel(channel);
    } else {
      connection.addEventListener('datachannel', this.handleDataChannel);
    }
  }

  sendText(value: string): ChatSendResult {
    const text = sanitizeChatText(value);
    if (text === '') {
      return { ok: false, reason: 'empty' };
    }
    if (countCodePoints(text) > CHAT_LIMITS.messageCodePoints) {
      return { ok: false, reason: 'too-long' };
    }
    if (!this.isOpen()) {
      return { ok: false, reason: 'unavailable' };
    }

    const message: ChatEntry = {
      id: this.createMessageId(),
      sender: 'self',
      text,
      sentAt: this.now(),
    };
    const wireMessage = createChatMessage('chat.message', {
      messageId: message.id,
      sentAt: message.sentAt,
      text: message.text,
    });
    if (!this.sendWire(wireMessage)) {
      return { ok: false, reason: 'send-failed' };
    }

    this.rememberMessageId(message.id);
    this.appendMessage(message);
    this.setTyping(false);
    return { ok: true, message };
  }

  setTyping(active: boolean): boolean {
    this.clearLocalTypingTimer();
    if (!this.isOpen()) {
      this.localTyping = false;
      return false;
    }

    if (active !== this.localTyping) {
      const sent = this.sendWire(createChatMessage('chat.typing', { active, sentAt: this.now() }));
      if (!sent) {
        return false;
      }
      this.localTyping = active;
    }

    if (active) {
      this.localTypingTimer = setTimeout(
        () => this.setTyping(false),
        this.options.localTypingIdleMs ?? LOCAL_TYPING_IDLE_MS,
      );
    }
    return true;
  }

  setPanelVisible(visible: boolean): void {
    this.panelVisible = visible;
    if (visible && this.unreadCount > 0) {
      this.unreadCount = 0;
      this.publish();
    }
  }

  endSession(): void {
    if (this._state === 'ended') {
      return;
    }
    this._state = 'ended';
    this.clearTypingState();
    this.releaseConnection();
    this.messages.length = 0;
    this.seenMessageIds.clear();
    this.seenMessageOrder.length = 0;
    this.unreadCount = 0;
    writeLog(this.logger, 'info', 'chat', 'session.ended', {});
    this.publish();
    this.clearListeners();
  }

  private readonly handleDataChannel = (event: RTCDataChannelEvent) => {
    if (event.channel.label !== CHAT_CHANNEL_LABEL) {
      event.channel.close();
      return;
    }
    this.attachChannel(event.channel);
  };

  private attachChannel(channel: RTCDataChannel): void {
    if (!isReliableOrderedChatChannel(channel)) {
      channel.close();
      this.reportError('invalid-channel', 'Peer offered an invalid chat data channel.');
      return;
    }

    this.releaseChannel();
    this.channel = channel;
    channel.addEventListener('open', this.handleOpen);
    channel.addEventListener('message', this.handleMessage);
    channel.addEventListener('close', this.handleClose);
    channel.addEventListener('error', this.handleError);
    if (channel.readyState === 'open') {
      this.handleOpen();
    }
  }

  private readonly handleOpen = () => {
    this.setState('open');
    writeLog(this.logger, 'info', 'chat', 'channel.opened', {});
  };

  private readonly handleMessage = (event: MessageEvent<unknown>) => {
    if (typeof event.data !== 'string') {
      this.reportError('invalid-message', 'Chat data must be a text frame.');
      return;
    }
    const parsed = parseChatMessage(event.data);
    if (!parsed.ok) {
      this.reportError('invalid-message', `Invalid chat message: ${parsed.reason}.`);
      return;
    }
    this.processMessage(parsed.message);
  };

  private processMessage(message: AnyChatWireMessage): void {
    if (message.type === 'chat.typing') {
      this.setPeerTyping(message.payload.active);
      return;
    }

    if (this.seenMessageIds.has(message.payload.messageId)) {
      return;
    }
    const text = sanitizeChatText(message.payload.text);
    if (text === '') {
      this.reportError('invalid-message', 'Chat message was empty after sanitization.');
      return;
    }
    this.rememberMessageId(message.payload.messageId);
    this.setPeerTyping(false);
    this.appendMessage({
      id: message.payload.messageId,
      sender: 'peer',
      text,
      sentAt: message.payload.sentAt,
    });
  }

  private readonly handleClose = () => {
    this.clearTypingState();
    if (this._state !== 'ended') {
      this.setState('reconnecting');
    }
  };

  private readonly handleError = () => {
    this.reportError('channel-error', 'Chat data channel failed.');
    if (this._state !== 'ended') {
      this.setState('failed');
    }
  };

  private setPeerTyping(active: boolean): void {
    this.clearRemoteTypingTimer();
    const changed = this.peerTyping !== active;
    this.peerTyping = active;
    if (active) {
      this.remoteTypingTimer = setTimeout(() => {
        this.remoteTypingTimer = null;
        this.peerTyping = false;
        this.publish();
      }, this.options.remoteTypingExpiryMs ?? REMOTE_TYPING_EXPIRY_MS);
    }
    if (changed) {
      this.publish();
    }
  }

  private appendMessage(message: ChatEntry): void {
    this.messages.push(message);
    if (this.messages.length > CHAT_HISTORY_LIMIT) {
      this.messages.splice(0, this.messages.length - CHAT_HISTORY_LIMIT);
    }
    if (message.sender === 'peer' && !this.panelVisible) {
      this.unreadCount = Math.min(this.unreadCount + 1, CHAT_HISTORY_LIMIT);
    }
    this.publish();
  }

  private rememberMessageId(messageId: string): void {
    this.seenMessageIds.add(messageId);
    this.seenMessageOrder.push(messageId);
    while (this.seenMessageOrder.length > DEDUPLICATION_LIMIT) {
      const oldest = this.seenMessageOrder.shift();
      if (oldest !== undefined) {
        this.seenMessageIds.delete(oldest);
      }
    }
  }

  private sendWire(message: AnyChatWireMessage): boolean {
    const channel = this.channel;
    if (channel === null || channel.readyState !== 'open') {
      return false;
    }
    if (channel.bufferedAmount > (this.options.maxBufferedAmount ?? MAX_BUFFERED_AMOUNT)) {
      this.reportError('send-failed', 'Chat is waiting for the connection to catch up.');
      return false;
    }
    try {
      channel.send(JSON.stringify(message));
      writeLog(this.logger, 'debug', 'chat', 'message.sent', { type: message.type });
      return true;
    } catch (cause) {
      this.reportError('send-failed', 'Could not send chat message.', cause);
      return false;
    }
  }

  private isOpen(): boolean {
    return this._state === 'open' && this.channel?.readyState === 'open';
  }

  private releaseConnection(): void {
    this.connection?.removeEventListener('datachannel', this.handleDataChannel);
    this.connection = null;
    this.releaseChannel();
  }

  private releaseChannel(): void {
    const channel = this.channel;
    this.channel = null;
    if (channel === null) {
      return;
    }
    channel.removeEventListener('open', this.handleOpen);
    channel.removeEventListener('message', this.handleMessage);
    channel.removeEventListener('close', this.handleClose);
    channel.removeEventListener('error', this.handleError);
    if (channel.readyState !== 'closed') {
      channel.close();
    }
  }

  private clearTypingState(): void {
    this.clearLocalTypingTimer();
    this.clearRemoteTypingTimer();
    this.localTyping = false;
    this.peerTyping = false;
  }

  private clearLocalTypingTimer(): void {
    if (this.localTypingTimer !== null) {
      clearTimeout(this.localTypingTimer);
      this.localTypingTimer = null;
    }
  }

  private clearRemoteTypingTimer(): void {
    if (this.remoteTypingTimer !== null) {
      clearTimeout(this.remoteTypingTimer);
      this.remoteTypingTimer = null;
    }
  }

  private setState(state: ChatChannelState): void {
    if (this._state === state) {
      return;
    }
    const previous = this._state;
    this._state = state;
    writeLog(this.logger, 'info', 'chat', 'state.changed', { previous, current: state });
    this.publish();
  }

  private publish(): void {
    this.emit('change', { snapshot: this.snapshot });
  }

  private reportError(
    code: ConstructorParameters<typeof ChatError>[0],
    message: string,
    cause?: unknown,
  ): void {
    const error = new ChatError(code, message, cause === undefined ? undefined : { cause });
    writeLog(this.logger, 'warn', 'chat', 'operation.failed', { code, state: this._state });
    this.emit('error', { error });
  }
}

function isReliableOrderedChatChannel(channel: RTCDataChannel): boolean {
  return (
    channel.label === CHAT_CHANNEL_LABEL &&
    channel.protocol === CHAT_CHANNEL_PROTOCOL &&
    channel.ordered &&
    channel.maxPacketLifeTime === null &&
    channel.maxRetransmits === null
  );
}
